import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, inr, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Sparkles, Plus, Store } from "lucide-react";
import { toast } from "sonner";

const norm = (s) => (s || "").toLowerCase();
const matchCat = (svcType, catName) => {
  const t = norm(svcType);
  if (!t) return false;
  const words = norm(catName).split(/[^a-z]+/).filter((w) => w.length > 2);
  return words.some((w) => t.includes(w) || w.includes(t));
};

export default function ByService() {
  const { addItem } = useCart();
  const [active, setActive] = useState(null);
  const { data: cats = [] } = useQuery({ queryKey: ["service-categories"], queryFn: async () => (await api.get("/service-categories")).data });
  const { data: services = [] } = useQuery({ queryKey: ["all-services"], queryFn: async () => (await api.get("/services")).data });
  const { data: vendors = [] } = useQuery({ queryKey: ["all-vendors"], queryFn: async () => (await api.get("/vendors")).data });
  const vname = (id) => vendors.find((v) => v.id === id)?.name || "Vendor";

  const selected = active || (cats[0]?.name);
  const listings = services.filter((s) => matchCat(s.type, selected || ""));

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 pb-24" data-testid="by-service">
      <div className="flex items-center gap-2 text-primary font-semibold text-sm"><Sparkles className="h-4 w-4" /> By Service</div>
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mt-1">Browse by service</h1>
      <p className="text-muted-foreground mt-2">Pick a service and choose from trusted local providers.</p>

      <div className="flex flex-wrap gap-2 mt-6">
        {cats.map((c) => (
          <button
            key={c.id}
            data-testid={`svc-cat-${c.id}`}
            onClick={() => setActive(c.name)}
            className={`h-10 px-4 rounded-full text-sm font-semibold transition-colors ${selected === c.name ? "bg-primary text-white" : "border border-stone-300 hover:bg-accent"}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {selected && (
        <p className="text-sm text-muted-foreground mt-4">{cats.find((c) => c.name === selected)?.description}</p>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4" data-testid="svc-listings">
        {listings.map((s) => (
          <div key={s.id} data-testid={`svc-item-${s.id}`} className="bg-card border border-stone-200 rounded-2xl overflow-hidden flex flex-col">
            {s.image ? <img src={imgUrl(s.image)} alt={s.name} className="h-40 w-full object-cover" /> : <div className="h-40 w-full bg-gradient-to-br from-primary/20 to-secondary/20" />}
            <div className="p-4 flex-1 flex flex-col">
              <h3 className="font-semibold">{s.name}</h3>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Store className="h-3 w-3" /> {vname(s.vendor_id)}</p>
              {s.desc && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{s.desc}</p>}
              <div className="mt-auto pt-3 flex items-center justify-between">
                <span className="font-display font-extrabold">{inr(s.price)}<span className="text-xs text-muted-foreground font-normal"> {s.unit}</span></span>
                <button
                  data-testid={`svc-add-${s.id}`}
                  onClick={() => { addItem(s, { id: s.vendor_id, name: vname(s.vendor_id) }); toast.success("Added to cart"); }}
                  className="h-9 px-4 bg-primary text-white rounded-full text-sm font-semibold flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selected && listings.length === 0 && (
        <div className="text-center text-muted-foreground py-16" data-testid="svc-empty">
          No listings yet in <b>{selected}</b> — check back soon or ask our AI planner.
        </div>
      )}
    </div>
  );
}
